
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Scene from "../Scene";
import { useState, useEffect } from "react";
import { Loader2, Zap, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SimulationDialogProps {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  selectedDrill: any | null;
}

const SimulationDialog = ({ showDialog, setShowDialog, selectedDrill }: SimulationDialogProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [highQualityMode, setHighQualityMode] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setUserProfile(data);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    console.log("SimulationDialog useEffect, showDialog:", showDialog);
    console.log("Selected drill:", selectedDrill);
    
    if (showDialog) {
      setIsLoading(true);
      console.log("Setting loading state to true");
      // Reset loading state after a short delay to allow the scene to initialize
      const timer = setTimeout(() => {
        console.log("Setting loading state to false after timeout");
        setIsLoading(false);
      }, 2000);
      return () => {
        console.log("Clearing timeout on cleanup");
        clearTimeout(timer);
      };
    }
  }, [showDialog, selectedDrill]);

  const toggleHighQuality = () => {
    // Only allow high quality mode for paid users
    if (!userProfile?.paid_account) {
      toast({
        title: "Premium Feature",
        description: "Advanced rendering is only available on Pro and Enterprise plans",
        variant: "destructive",
      });
      return;
    }

    console.log("Toggling high quality mode from", highQualityMode, "to", !highQualityMode);
    if (!highQualityMode) {
      // When enabling high quality, reset the loading state
      setIsLoading(true);
      setHighQualityMode(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    } else {
      setHighQualityMode(false);
    }
  };

  const isPaidUser = userProfile?.paid_account;

  return (
    <Dialog open={showDialog} onOpenChange={(open) => {
      console.log("Dialog open state changing to:", open);
      setShowDialog(open);
    }}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Drill Simulation</span>
            <div className="flex items-center space-x-2">
              <Label htmlFor="high-quality-mode" className="text-sm font-normal flex items-center">
                {!isPaidUser && <Lock className="w-4 h-4 mr-1 text-gray-400" />}
                {isPaidUser && <Zap className="w-4 h-4 mr-1 text-yellow-500" />}
                Advanced Rendering
              </Label>
              <Switch
                id="high-quality-mode"
                checked={highQualityMode}
                onCheckedChange={toggleHighQuality}
                disabled={!isPaidUser}
              />
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 h-[600px] relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-50">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          
          {!isPaidUser && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm z-40">
              <Lock className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">3D Simulations</h3>
              <p className="text-center text-muted-foreground mb-4 max-w-md">
                Full 3D simulations are available exclusively on Pro and Enterprise plans
              </p>
              <Button onClick={() => window.location.href = '/prices'}>
                Upgrade Your Plan
              </Button>
            </div>
          )}
          
          <Scene 
            drillText={selectedDrill?.description} 
            highQuality={highQualityMode && isPaidUser} 
          />
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {!isPaidUser ? 
            "Upgrade to Pro or Enterprise for full 3D simulations and advanced rendering features." : 
            highQualityMode ? 
              "Advanced rendering enabled - Using higher quality models and lighting for precise visualization." : 
              "Standard rendering mode - Toggle advanced rendering for more detailed visualization."
          }
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SimulationDialog;
